import React, { useEffect } from "react";
import {
  golfstatusColors,
  GSForm,
  GSItemList,
  GSSidePanelPage,
} from "golfstatus_react_components";
import {
  faBolt,
  faChevronLeft,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

//import scss
import "./tournament-round-auto-assign-all.scss";
import {
  autoAssignHoles,
  clearRoundNotifications,
  getLinkedRoundHoleAssignments,
  selectCurrentRound,
  selectCurrentRoundHoleAssignments,
  selectLinkedRoundHoleAssignments,
  selectLoading,
  selectRoundNotifications,
} from "../../../../reducers/roundSlice";
import {
  getTournamentTeams,
  selectQualifiedTeams,
  selectLoading as selectTeamsLoading,
} from "../../../../reducers/teamsSlice";
import { useNotificationBanner } from "../../../../hooks/notificationHooks";
import { useRoundHooks, getRoundName, getSubRounds } from "../../../../hooks/round/roundHooks";
import { filterUnassignedTeams } from "../../../../hooks/round/roundHoleAssignmentHooks";
import TournamentRoundLineItem from "../tournament-round-line-item";

//Name the component
const TournamentRoundAutoAssignAll = () => {
  //hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const match = useMatch(
    "/:tournamentID/rounds/:roundID/hole-assignments/auto-assign/assign-all",
  );

  //selectors
  const currentRound = useSelector(selectCurrentRound);
  const notifications = useSelector(selectRoundNotifications);
  const loading = useSelector(selectLoading);
  const tournamentTeams = useSelector(selectQualifiedTeams);
  const currentRoundHoleAssignments = useSelector(
    selectCurrentRoundHoleAssignments,
  );
  const linkedRoundHoleAssignments = useSelector(
    selectLinkedRoundHoleAssignments,
  );
  const teamsLoading = useSelector(selectTeamsLoading);

  const { allRounds } = useRoundHooks();

  //the assignment the auto assign form put together and what it placed, carried
  //over with the navigation rather than worked out again here
  const assignment = location?.state?.assignment;
  const assignedTeams = location?.state?.assignedTeams;

  const linkedRounds = getSubRounds(currentRound, allRounds);
  const otherRounds = linkedRounds?.filter?.(
    (r) => r?.id !== match?.params?.roundID,
  );

  const roundLabel = getRoundName(currentRound, allRounds);

  //a team can only play a round number once, so what is left to place is the
  //teams that hold no assignment in this round or the ones linked to it
  const remainingTeams = filterUnassignedTeams(tournamentTeams, [
    ...currentRoundHoleAssignments,
    ...linkedRoundHoleAssignments,
  ]).length;

  //a stable key, so the fetch doesn't re-run on every render of the derived list
  const otherRoundIDs = otherRounds?.map?.((r) => r?.id)?.join?.(",");

  //the count only means anything once the teams and the linked rounds are in
  const remainingTeamsCounted =
    tournamentTeams?.length > 0 &&
    !teamsLoading?.includes?.("getTournamentTeams") &&
    !loading?.includes?.("getLinkedRoundHoleAssignments");

  const leftNavigation = () => {
    navigate(-1);
  };

  //effects
  useEffect(() => {
    return () => {
      dispatch(clearRoundNotifications());
    };
  }, [dispatch]);

  //what is left to place depends on the teams and on what the linked rounds
  //already hold
  useEffect(() => {
    if (match?.params?.tournamentID) {
      dispatch(getTournamentTeams(match.params.tournamentID));
    }
  }, [dispatch, match?.params?.tournamentID]);

  useEffect(() => {
    if (!otherRoundIDs) {
      return;
    }
    dispatch(
      getLinkedRoundHoleAssignments({
        roundID: match?.params?.roundID,
        roundIDs: otherRoundIDs.split(","),
      }),
    );
  }, [dispatch, otherRoundIDs, match?.params?.roundID]);

  //nothing to assign without the form that sent us here
  useEffect(() => {
    if (!assignment) {
      navigate(-1);
    }
  }, [assignment, navigate]);

  //the round that was just assigned took the whole field, so there is nothing
  //left for the other rounds to be asked about
  useEffect(() => {
    if (remainingTeamsCounted && remainingTeams === 0) {
      navigate(-1);
    }
  }, [remainingTeamsCounted, remainingTeams, navigate]);

  //one call for the rest of them, so the server places them together
  const assignRemainingRounds = () => {
    dispatch(autoAssignHoles({ ...assignment, assignAllRounds: true })).then(
      (response) => {
        //the banner carries a failure, so the page stays put
        if (response?.meta?.requestStatus !== "fulfilled") {
          return;
        }
        navigate(-1);
      },
    );
  };

  const [banner] = useNotificationBanner({
    notifications,
    timeoutAction: () => dispatch(clearRoundNotifications()),
  });

  const getNavigation = () => {
    return {
      title: `Round ${roundLabel} Assigned`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const getFormTitle = () => {
    return (
      <div className="assign-header">
        <div className="icon">
          <FontAwesomeIcon
            icon={faCircleCheck}
            style={{ color: golfstatusColors.green400 }}
          ></FontAwesomeIcon>
        </div>
        {`Round ${roundLabel} Assigned`}
      </div>
    );
  };

  const getRoundList = () => {
    return (
      <GSItemList
        type="vertical large-gap"
        items={otherRounds}
        listItem={(round) => (
          <TournamentRoundLineItem
            round={round}
            linkedRounds={allRounds}
            hideActions
            hideCopy
          />
        )}
      ></GSItemList>
    );
  };

  const getFormSections = () => {
    return [
      {
        type: "vertical xx-large-gap",
        fields: [
          {
            value: (
              <div className="assign-container full-width">
                <div className="disclaimer">
                  {`${assignedTeams} teams have been assigned to Round ${roundLabel}. Would you like to use the same settings and assign the remaining ${remainingTeams} teams to the following rounds:`}
                </div>
                <div className="rounds">{getRoundList()}</div>
              </div>
            ),
            customView: true,
          },
        ],
      },
    ];
  };

  const getContent = () => {
    return (
      <GSForm formTitle={getFormTitle()} formSections={getFormSections()} />
    );
  };

  const getDrawer = () => {
    return {
      actions: [
        {
          name: "Auto Assign All",
          action: assignRemainingRounds,
          type: "black",
          buttonIcon: faBolt,
        },
        {
          name: "Close",
          action: () => navigate(-1),
          type: "grey",
        },
      ],
    };
  };

  return (
    //name the component tag
    <tournament-round-auto-assign-all>
      <GSSidePanelPage
        header={getNavigation()}
        banner={banner}
        content={getContent()}
        drawer={getDrawer()}
        loading={loading?.includes("autoAssignHoles") || !remainingTeamsCounted}
        loadingText="Auto Assigning Holes..."
      ></GSSidePanelPage>
    </tournament-round-auto-assign-all>
  );
};

//export the new name
export default TournamentRoundAutoAssignAll;
